import { useCallback, useEffect, useState } from 'react';

import type { MapTransformRequest } from 'src/common/external/aws/createMapTransformRequest';
import { createMapTransformRequest } from 'src/common/external/aws/createMapTransformRequest';

export interface MapTransformRequestState {
  /** The signer, or `undefined` while the first build is in flight. */
  transformRequest: MapTransformRequest | undefined;
  /** True if the build failed; callers should surface a retry. */
  error: boolean;
  /** Re-attempts the build (clears the error and rebuilds). */
  retry: () => void;
}

/**
 * Resolves a SigV4 transformRequest for ALS V2 maps, built once on mount.
 *
 * No interval/rebuild: react-map-gl reads `transformRequest` only at map
 * construction, and the auth helper internally refreshes the underlying
 * credentials (pulling a current Cognito id token via its logins provider) for
 * the life of the map — the transformRequest reads the latest creds on each
 * request. So a single function keeps multi-hour sessions signing correctly.
 *
 * `transformRequest` is `undefined` until the first build resolves; callers
 * render a loading state meanwhile. If the build rejects (e.g. a transient
 * network/auth failure on mount), `error` flips true so callers can show a
 * retry affordance rather than spinning forever — `retry()` rebuilds.
 */
export function useMapTransformRequest(): MapTransformRequestState {
  const [transformRequest, setTransformRequest] =
    useState<MapTransformRequest>();
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setError(false);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let canceled = false;

    createMapTransformRequest()
      .then((fn) => {
        if (!canceled) {
          // Functional setState: the stored value is itself a function, so a
          // bare setTransformRequest(fn) would be treated as an updater.
          setTransformRequest(() => fn);
        }
      })
      .catch((e) => {
        if (!canceled) {
          setError(true);
        }
        console.error('Failed to build map transformRequest', e);
      });

    return () => {
      canceled = true;
    };
  }, [attempt]);

  return { transformRequest, error, retry };
}
